/**
 * Lokale Kopie der GET-Antworten, Schlüssel = URL. Online kommt alles frisch vom Server
 * und wird hier abgelegt; offline liefert `request()` die abgelegte Antwort.
 */
import type { CombatActionResult, CombatDTO, MapDTO, NoteDetail } from '@kartothek/shared';
import { idbBatch, idbDelete, idbGet, idbKeys, idbPut, prefixRange } from './idb';

export const campaignPrefix = (cid: string) => `/api/campaigns/${cid}`;
export const noteUrl = (cid: string, id: string) => `${campaignPrefix(cid)}/notes/${id}`;
export const mapUrl = (cid: string, id: string) => `${campaignPrefix(cid)}/maps/${id}`;
export const combatUrl = (cid: string, id: string) => `${campaignPrefix(cid)}/combats/${id}`;

/** Diese Antworten gehören nicht in die lokale Kopie. */
const NOT_CACHED = [/^\/api\/screen\//, /^\/api\/polls\//, /\/sync(\?|$)/, /\/search\?/, /\/export(\?|$)/, /^\/api\/import\//, /^\/api\/invites\//, /\/invites(\?|$)/, /^\/api\/users/];

export function isCacheable(url: string): boolean {
  return url.startsWith('/api/') && !NOT_CACHED.some((re) => re.test(url));
}

interface Entry {
  data: unknown;
  at: number;
}

export async function readCached<T>(url: string): Promise<T | undefined> {
  return (await idbGet<Entry>('responses', url))?.data as T | undefined;
}

export async function writeCached(url: string, data: unknown): Promise<void> {
  if (!isCacheable(url)) return;
  await idbPut('responses', { data, at: Date.now() } satisfies Entry, url);
}

export async function writeManyCached(entries: { url: string; data: unknown }[]): Promise<void> {
  const at = Date.now();
  await idbBatch(
    'responses',
    entries.map((e) => ({ key: e.url, value: { data: e.data, at } satisfies Entry })),
  );
}

export async function deleteCached(urls: string[]): Promise<void> {
  await idbBatch(
    'responses',
    urls.map((key) => ({ key, delete: true })),
  );
}

export async function cachedUrls(prefix: string): Promise<string[]> {
  return idbKeys('responses', prefixRange(prefix));
}

export async function dropCampaign(cid: string): Promise<void> {
  await idbDelete('responses', prefixRange(campaignPrefix(cid)));
}

const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);

export function looksLikeNote(x: unknown): x is NoteDetail {
  return isObj(x) && typeof x.id === 'string' && typeof x.campaignId === 'string' && typeof x.body === 'string' && isObj(x.links);
}

export function looksLikeMap(x: unknown): x is MapDTO {
  return isObj(x) && typeof x.noteId === 'string' && Array.isArray(x.tokens) && Array.isArray(x.pins);
}

export function looksLikeCombat(x: unknown): x is CombatDTO {
  return isObj(x) && typeof x.id === 'string' && typeof x.campaignId === 'string' && Array.isArray(x.combatants);
}

/**
 * Antworten schreibender Anfragen enthalten oft das geänderte Objekt –
 * direkt in die lokale Kopie übernehmen, damit sie ohne weiteren Abruf aktuell bleibt.
 */
export async function storeWriteResponse(url: string, data: unknown): Promise<void> {
  const m = /^\/api\/campaigns\/([0-9a-f-]{36})\//i.exec(url);
  if (!m) return;
  const cid = m[1]!;
  if (looksLikeNote(data)) return writeCached(noteUrl(cid, data.id), data);
  if (looksLikeMap(data)) return writeCached(mapUrl(cid, data.noteId), data);
  if (looksLikeCombat(data)) return writeCached(combatUrl(cid, data.id), data);
  const action = data as CombatActionResult | undefined;
  if (isObj(action) && looksLikeCombat(action.combat)) return writeCached(combatUrl(cid, action.combat.id), action.combat);
}
